import { Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { parseDayInput, stepLabel } from './cards';
import { type CivilDay, PRESETS, type ResolvedFrame, dayString } from './frames';

/**
 * The page's window, as one control.
 *
 * It used to be a strip of every preset at once, plus the same dates again
 * under the greeting. In the dispatch frame that strip wrapped under the
 * host's shortcuts and read as a second toolbar. The name and the dates are
 * the same fact, so they sit together, and the other presets stay one click
 * away rather than on the page. The steps stay outside the menu: a dispatcher
 * on Today wants yesterday next, not a picker.
 */

const GROUP_LABELS: Record<'recent' | 'calendar' | 'long', string> = {
  recent: 'Recent',
  calendar: 'Calendar',
  long: 'Longer',
};

export function WindowPicker({
  frame,
  frameKey,
  customActive,
  rangeLabel,
  canStepForward,
  onStep,
  onPick,
  onCustom,
}: {
  frame: ResolvedFrame;
  frameKey: string;
  customActive: boolean;
  rangeLabel: string;
  canStepForward: boolean;
  onStep: (direction: -1 | 1) => void;
  onPick: (key: string) => void;
  onCustom: (range: { from: CivilDay; to: CivilDay }) => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState<CivilDay>(frame.from);
  const [to, setTo] = useState<CivilDay>(frame.to);

  const name = customActive
    ? 'Custom range'
    : (PRESETS.find((preset) => preset.key === frameKey)?.label ?? frame.label);

  const reveal = () => {
    setFrom(frame.from);
    setTo(frame.to);
    setCustomOpen(customActive);
    setOpen((was) => !was);
  };

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = (['recent', 'calendar', 'long'] as const).map((group) => ({
    group,
    presets: PRESETS.filter((preset) => preset.group === group),
  }));

  return (
    <div className="db-window" ref={rootRef}>
      <button
        type="button"
        className="db-step"
        onClick={() => onStep(-1)}
        title={stepLabel(-1, frame.days)}
        aria-label={stepLabel(-1, frame.days)}
      >
        <ChevronLeft size={15} />
      </button>
      <button
        type="button"
        className="db-window-pick"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="db-window-menu"
        aria-label={`Time window: ${name}, ${rangeLabel}. Change it`}
        onClick={reveal}
      >
        <span className="db-window-name">{name}</span>
        <span className="db-range-label">{rangeLabel}</span>
        <ChevronDown size={14} className="db-window-caret" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="db-step"
        onClick={() => onStep(1)}
        disabled={!canStepForward}
        title={stepLabel(1, frame.days)}
        aria-label={stepLabel(1, frame.days)}
      >
        <ChevronRight size={15} />
      </button>

      {open ? (
        <div id="db-window-menu" className="db-window-menu" role="dialog" aria-label="Time window">
          {groups.map(({ group, presets }) => (
            <div key={group} className="db-window-group">
              <p>{GROUP_LABELS[group]}</p>
              {presets.map((preset) => {
                const on = frameKey === preset.key && !customActive;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    className={on ? 'db-window-opt on' : 'db-window-opt'}
                    aria-pressed={on}
                    onClick={() => {
                      onPick(preset.key);
                      setOpen(false);
                    }}
                  >
                    {preset.label}
                    {on ? <Check size={14} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          ))}

          <div className="db-window-group">
            <p>Your own</p>
            <button
              type="button"
              className={customActive ? 'db-window-opt on' : 'db-window-opt'}
              aria-expanded={customOpen}
              onClick={() => setCustomOpen((was) => !was)}
            >
              Custom range
              <ChevronDown size={14} aria-hidden="true" className={customOpen ? 'db-turned' : undefined} />
            </button>
            {customOpen ? (
              <div className="db-custom">
                <label>
                  From
                  <input
                    type="date"
                    defaultValue={dayString(from)}
                    onChange={(event) => {
                      const day = parseDayInput(event.target.value);
                      if (day) setFrom(day);
                    }}
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    defaultValue={dayString(to)}
                    onChange={(event) => {
                      const day = parseDayInput(event.target.value);
                      if (day) setTo(day);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    onCustom({ from, to });
                    setOpen(false);
                  }}
                >
                  Apply
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
